import {Component, DestroyRef, OnInit, inject, ViewChildren, QueryList} from '@angular/core';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {skip} from "rxjs/operators";
import {CardModule} from "primeng/card";
import {ButtonModule} from "primeng/button";
import {TooltipModule} from "primeng/tooltip";
import {ChartModule, UIChart} from "primeng/chart";
import {DashboardService} from "../../core/service/dashboard.service";
import {CategoriesService} from "../commons/categories/categories.service";
import {Category} from "../commons/categories/category.model";
import {DashboardResponseModule, TopProduct, RevenueTrendItem} from "../../core/modules/dashboaard/dashboard-response.module";
import {TagModule} from "primeng/tag";
import {DividerModule} from "primeng/divider";
import {SelectButtonModule} from "primeng/selectbutton";
import {FormsModule} from "@angular/forms";
import {TableModule} from "primeng/table";
import {NgForOf, NgIf, NgTemplateOutlet, DatePipe, DecimalPipe} from "@angular/common";
import {CalendarModule} from "primeng/calendar";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {Params, RouterLink} from "@angular/router";
import {LocaleService} from "../../core/i18n/locale.service";
import {BuyerTypeEnum} from "../../core/modules/enums/buyer-type.enum";
import { TableLoadingDirective } from '../../core/directive/table-loading.directive';

/**
 * One buyer type's counts. Individual + Branch = total, and each already includes its
 * not-started accounts (badged, not added). Incomplete and active count businesses only.
 */
interface BuyerGroupCounts {
  total: number;
  individual: number;
  individualIncomplete: number;
  individualNotStarted: number;
  branch: number;
  branchIncomplete: number;
  branchNotStarted: number;
  active: number;
}

interface PopulationSegment {
  key: string;
  labelKey: string;
  count: number;
  /** Suffix of a .dash-accent-* class. */
  accent: string;
}

const emptyBuyerGroup = (): BuyerGroupCounts => ({
  total: 0, individual: 0, individualIncomplete: 0, individualNotStarted: 0,
  branch: 0, branchIncomplete: 0, branchNotStarted: 0, active: 0,
});

/**
 * /buyers?view=all filters matching each number on a Restaurants/Hotels card
 * (fe-guide §2.4 — every dashboard number equals the list it opens). Active is not
 * linked: the card counts active businesses, while the list's isActive is per account.
 */
function buyerGroupLinks(buyerType: BuyerTypeEnum) {
  const base: Params = { view: 'all', buyerType };
  return {
    total:                base,
    individual:           { ...base, scope: 'Individual' },
    individualIncomplete: { ...base, scope: 'Individual', isIncomplete: true },
    individualNotStarted: { ...base, scope: 'Individual', hasOrganization: false },
    branch:               { ...base, scope: 'Branch' },
    branchIncomplete:     { ...base, scope: 'Branch', isIncomplete: true },
    branchNotStarted:     { ...base, scope: 'Branch', hasOrganization: false },
  } satisfies Record<string, Params>;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    TableLoadingDirective,
    CardModule,
    ButtonModule,
    TooltipModule,
    ChartModule,
    TagModule,
    DividerModule,
    SelectButtonModule,
    FormsModule,
    TableModule,
    NgForOf,
    NgIf,
    NgTemplateOutlet,
    DatePipe,
    DecimalPipe,
    CalendarModule,
    TranslatePipe,
    RouterLink,
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.css'
})
export class DashboardComponent implements OnInit{
  private readonly translate = inject(TranslateService);
  private readonly localeService = inject(LocaleService);
  private readonly destroyRef = inject(DestroyRef);

  // ========== TABS ==========
  // Panels hide via CSS (not *ngIf) so charts and the lazy products table
  // aren't destroyed/recreated — and don't refetch or re-render — on every
  // tab switch. Grouping: population/activity, money, catalog.
  activeTab: 'overview' | 'revenue' | 'catalog' = 'overview';

  // All p-charts across the tab panels. Panels hide via display:none, so a
  // chart built while its panel was hidden was measured against a 0-height
  // box — Chart.js then renders it at the wrong size (the revenue line chart
  // clipped at the card bottom on first view, and only snapped right once the
  // period toggle rebuilt it). reinit() rebuilds each chart against its now-
  // visible, correctly-measured container.
  @ViewChildren(UIChart) private charts!: QueryList<UIChart>;

  setTab(tab: 'overview' | 'revenue' | 'catalog'): void {
    this.activeTab = tab;
    this.reinitCharts();
  }

  /** Rebuild every chart against its current (visible) container size. Deferred
   *  across two frames so the panel's display:block + layout have fully
   *  settled before Chart.js re-measures (a 0ms timeout can fire too early). */
  private reinitCharts(): void {
    requestAnimationFrame(() =>
      requestAnimationFrame(() =>
        this.charts?.forEach(c => (c as any).reinit())
      )
    );
  }

  /**
   * Format a value with the currency symbol on the locale-correct side:
   * before the number in LTR (English) — "SAR 1,200" — and after it in RTL
   * (Arabic) — "1,200 ر.س".
   */
  formatCurrency(value: unknown): string {
    const currency = this.translate.instant('dashboard.currencyPrefix');
    const amount = value ?? '';
    return this.localeService.current === 'ar' ? `${amount} ${currency}` : `${currency} ${amount}`;
  }

  // ========== DATE FILTER ==========
  today: Date = new Date();
  startDate: Date = new Date(this.today.getFullYear(), this.today.getMonth(), 1);
  endDate: Date = new Date();

  // ========== Sales Summary (from API) ==========
  totalRevenue: number = 0;
  revenueGrowthPercent: number = 0;
  averageOrderValue: number = 0;
  commissionRevenue: number = 0;

  // ========== Revenue Trends (from API) ==========
  revenueTrendPeriod: string = 'daily';
  revenueTrendOptions: any[] = [];
  revenueTrendDaily: RevenueTrendItem[] = [];
  revenueTrendWeekly: RevenueTrendItem[] = [];
  revenueTrendMonthly: RevenueTrendItem[] = [];
  revenueTrendChartData: any;
  revenueTrendChartOptions: any;

  // ========== Revenue by Category/Supplier (from API) ==========
  // categoryId is kept so we can resolve a localized name from the categories
  // list (the dashboard endpoint only returns one categoryName string).
  revenueByCategory: {id: string, name: string, revenue: number}[] = [];
  revenueBySupplier: {name: string, revenue: number}[] = [];

  /** id → { en, ar } for every category, loaded once from the Categories admin
   *  list. Lets the revenue-by-category chart show Arabic names in AR mode even
   *  though the dashboard endpoint sends a single (English) categoryName. */
  private categoryNames = new Map<string, { en: string; ar: string }>();
  revenueByCategoryChartData: any;
  revenueBySupplierChartData: any;
  revenueByCategoryChartOptions: any;
  revenueBySupplierChartOptions: any;

  // ========== BUSINESS ACCOUNTS (all-time — the date filter never applies to counts) ==========
  // Business accounts = owners + buyers (restaurants + hotels) + suppliers, each
  // including not-started registrations (no staff/financial users).
  accountsCount: number = 0;
  buyersCount: number = 0;
  restaurants: BuyerGroupCounts = emptyBuyerGroup();
  hotels: BuyerGroupCounts = emptyBuyerGroup();
  suppliers = { total: 0, notStarted: 0, active: 0, incomplete: 0 };
  owners = { total: 0, restaurant: 0, hotel: 0 };

  /** The All business accounts bar: the buyer side (restaurants, hotels, the owners
   *  of branch ones) first, then suppliers — the order the Overview reads. */
  populationSegments: PopulationSegment[] = [];

  // Deep links into the Buyers page's All view (query names = GET /admin/buyers params)
  // and the Suppliers page.
  readonly restaurantLinks = buyerGroupLinks(BuyerTypeEnum.Restaurant);
  readonly hotelLinks = buyerGroupLinks(BuyerTypeEnum.Hotel);
  readonly ownersLink: Params = { view: 'all', buyerType: BuyerTypeEnum.BusinessOwner };
  readonly suppliersNotStartedLink: Params = { hasOrganization: false };

  get categoryTotal(): number {
    return Math.round(this.revenueByCategory.reduce((sum, c) => sum + c.revenue, 0));
  }

  get supplierTotal(): number {
    return Math.round(this.revenueBySupplier.reduce((sum, s) => sum + s.revenue, 0));
  }

  // ========== CHART EMPTY-STATE FLAGS ==========
  get hasRevenueTrendData(): boolean {
    const data = this.revenueTrendChartData?.datasets?.[0]?.data ?? [];
    return data.some((v: number) => v > 0);
  }

  get hasCategoryData(): boolean {
    return this.revenueByCategory.length > 0;
  }

  get hasSupplierData(): boolean {
    return this.revenueBySupplier.length > 0;
  }

  get hasOrdersData(): boolean {
    const d = this.dashboardResponseModule;
    return !!d && ((d.approvedOrdersCount ?? 0) + (d.rejectedOrdersCount ?? 0) + (d.canceledOrdersCount ?? 0)) > 0;
  }

  get hasProductsData(): boolean {
    const d = this.dashboardResponseModule;
    return !!d && ((d.productsCount ?? 0) + (d.publishedProductsCount ?? 0) + (d.requestOfferProductsCount ?? 0)) > 0;
  }

  // ========== Top Performing Products (from API) ==========
  topProductsViewOptions: any[] = [];
  topProductsView: string = 'revenue';
  topProducts: TopProduct[] = [];
  topProductsByRevenue: TopProduct[] = [];
  topProductsByOrderCount: TopProduct[] = [];
  topProductsByQuantity: TopProduct[] = [];

  // Pagination
  first: number = 0;
  pageSize: number = 6;
  pageSizeOptions: number[] = [6, 10, 20, 50];

  // ========== EXISTING ==========
  orderChartData: any;
  orderChartOptions: any;
  ProductsChartData: any;
  ProductsChartOptions: any;
  dashboardResponseModule: DashboardResponseModule | undefined;

  constructor(
    private dashboardService: DashboardService,
    private categoriesService: CategoriesService
  ) {
  }

  /** Load the categories once and index them by id → { en, ar }. Rebuilds the
   *  category chart afterwards so labels pick up the localized names. */
  private loadCategoryNames(): void {
    this.categoriesService.getAll(1, 1000).subscribe({
      next: res => {
        this.categoryNames.clear();
        // Index top-level categories AND their subcategories, so the lookup
        // works whether the dashboard attributes revenue to a parent or a child.
        const index = (list: Category[] | undefined) => {
          for (const c of list ?? []) {
            if (c.id) this.categoryNames.set(c.id, { en: c.titleEn, ar: c.titleAr });
            index(c.subCategories);
          }
        };
        index(res?.items);
        // Data may already be present — re-render with the resolved names.
        if (this.revenueByCategory.length) this.buildRevenueByCategoryChart();
      },
      error: () => { /* fall back to the dashboard's categoryName */ }
    });
  }

  /** Localized display name for a category row: its titleAr/titleEn from the
   *  categories list, falling back to the dashboard's categoryName. */
  private categoryDisplayName(id: string, fallback: string): string {
    const entry = this.categoryNames.get(id);
    if (!entry) return fallback;
    const ar = this.localeService.current === 'ar';
    return (ar ? entry.ar : entry.en) || fallback;
  }

  ngOnInit() {
    // Build the translated select-button options + base chart configs for the
    // FIRST render (in whatever language is persisted). These are built with
    // translate.instant, so they aren't reactive — they must be set here, not
    // only inside the current$ subscription (which is skip(1)'d below).
    this.rebuildLocalizedOptions();
    this.initCharts();
    this.initRevenueTrendChart();

    // current$ is a BehaviorSubject: skip(1) drops its initial emission (the
    // first load is handled by the explicit setup above + loadDashboard()
    // below) so we only react to real language switches. On a switch we
    // rebuild the option labels and re-fetch — loadDashboard() rebuilds EVERY
    // data chart (orders, products, trends, category, supplier, top-products)
    // with the new language's labels and reinits them, so all tabs update.
    this.localeService.current$
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.rebuildLocalizedOptions();
        this.loadDashboard();
      });

    this.loadCategoryNames();
    this.loadDashboard();
  }

  private rebuildLocalizedOptions() {
    // Use get() (async) not instant(): the translation JSON is HTTP-loaded, so
    // on FIRST render instant() would return the raw keys ("dashboard.daily")
    // before the file arrives. get() waits for the active language to be loaded
    // and emits the resolved strings — correct on both first load and switch.
    // LocaleService applies translate.use() before emitting current$, so the
    // language is already the new one here. New array refs → p-selectButton
    // re-renders the relabelled options.
    const keys = [
      'dashboard.daily', 'dashboard.weekly', 'dashboard.monthly',
      'dashboard.byRevenue', 'dashboard.byOrders', 'dashboard.byQuantity'
    ];
    this.translate.get(keys).subscribe(t => {
      this.revenueTrendOptions = [
        { label: t['dashboard.daily'], value: 'daily' },
        { label: t['dashboard.weekly'], value: 'weekly' },
        { label: t['dashboard.monthly'], value: 'monthly' }
      ];
      this.topProductsViewOptions = [
        { label: t['dashboard.byRevenue'], value: 'revenue' },
        { label: t['dashboard.byOrders'], value: 'orders' },
        { label: t['dashboard.byQuantity'], value: 'quantity' }
      ];
    });
  }

  loadDashboard() {
    this.dashboardService.getDashboard(this.buildDateParams()).subscribe(data => {
      this.dashboardResponseModule = data.data as DashboardResponseModule;

      // Map new revenue metrics
      this.totalRevenue = this.dashboardResponseModule?.totalRevenue ?? 0;
      this.revenueGrowthPercent = this.dashboardResponseModule?.revenueGrowthPercentage ?? 0;
      this.averageOrderValue = this.dashboardResponseModule?.averageOrderValue ?? 0;
      this.commissionRevenue = this.dashboardResponseModule?.platformCommissionRevenue ?? 0;

      this.mapUserCounts(this.dashboardResponseModule);

      // Map revenue by category (top 10 only). Keep categoryId so the chart can
      // resolve a localized name from the categories list.
      this.revenueByCategory = (this.dashboardResponseModule?.revenueByCategory ?? [])
        .map(c => ({ id: c.categoryId, name: c.categoryName, revenue: c.revenue }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 10);

      // Map revenue by supplier (top 10 only)
      this.revenueBySupplier = (this.dashboardResponseModule?.revenueBySupplier ?? [])
        .map(s => ({ name: s.supplierName, revenue: s.revenue }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 10);

      // Build charts
      this.buildRevenueByCategoryChart();
      this.buildRevenueBySupplierChart();

      // Map top products
      this.topProductsByRevenue = this.dashboardResponseModule?.topProductsByRevenue ?? [];
      this.topProductsByOrderCount = this.dashboardResponseModule?.topProductsByOrderCount ?? [];
      this.topProductsByQuantity = this.dashboardResponseModule?.topProductsByQuantity ?? [];
      this.updateTopProductsView();

      // Map revenue trends
      this.revenueTrendDaily = this.dashboardResponseModule?.revenueTrends?.daily ?? [];
      this.revenueTrendWeekly = this.dashboardResponseModule?.revenueTrends?.weekly ?? [];
      this.revenueTrendMonthly = this.dashboardResponseModule?.revenueTrends?.monthly ?? [];
      this.updateRevenueTrendChart();

      this.refreshOrdersChart();

      // Data (and thus new chart configs) just landed — rebuild charts against
      // their real container sizes so the revenue line chart isn't clipped on
      // first paint / after a date-filter refetch.
      this.reinitCharts();
    });
  }

  private mapUserCounts(d: DashboardResponseModule | undefined): void {
    const n = (v: number | null | undefined) => v ?? 0;

    this.restaurants = {
      total: n(d?.totalRestaurantsCount),
      individual: n(d?.standaloneRestaurantsCount),
      individualIncomplete: n(d?.incompleteStandaloneRestaurantsCount),
      individualNotStarted: n(d?.standaloneRestaurantAccountsWithoutOrganizationCount),
      branch: n(d?.restaurantBranchesCount),
      branchIncomplete: n(d?.incompleteRestaurantBranchesCount),
      branchNotStarted: n(d?.branchRestaurantAccountsWithoutOrganizationCount),
      active: n(d?.activeStandaloneRestaurantsCount) + n(d?.activeBranchRestaurantsCount),
    };
    this.hotels = {
      total: n(d?.totalHotelsCount),
      individual: n(d?.standaloneHotelsCount),
      individualIncomplete: n(d?.incompleteStandaloneHotelsCount),
      individualNotStarted: n(d?.standaloneHotelAccountsWithoutOrganizationCount),
      branch: n(d?.hotelBranchesCount),
      branchIncomplete: n(d?.incompleteHotelBranchesCount),
      branchNotStarted: n(d?.branchHotelAccountsWithoutOrganizationCount),
      active: n(d?.activeStandaloneHotelsCount) + n(d?.activeBranchHotelsCount),
    };
    this.suppliers = {
      total: n(d?.suppliersCount),
      notStarted: n(d?.supplierAccountsWithoutOrganizationCount),
      active: n(d?.activeSuppliersCount),
      incomplete: n(d?.incompleteSuppliersAccountsCount),
    };
    this.owners = {
      total: n(d?.totalBusinessOwnersCount),
      restaurant: n(d?.restaurantBusinessOwnersCount),
      hotel: n(d?.hotelBusinessOwnersCount),
    };
    this.buyersCount = n(d?.totalBuyersCount);
    this.accountsCount = n(d?.totalBusinessAccountsCount);

    // Each segment's flex-grow is its count, so the bar splits by the parts' own
    // sum and always fills exactly, even if the API total and its parts disagree.
    this.populationSegments = [
      { key: 'restaurants', labelKey: 'dashboard.restaurants',    count: this.restaurants.total, accent: 'brand' },
      { key: 'hotels',      labelKey: 'dashboard.hotels',         count: this.hotels.total,      accent: 'blue' },
      { key: 'owners',      labelKey: 'dashboard.businessOwners', count: this.owners.total,      accent: 'orange' },
      { key: 'suppliers',   labelKey: 'dashboard.suppliers',      count: this.suppliers.total,   accent: 'purple' },
    ];
  }

  onDateFilterChange() {
    // The date range only affects totalRevenue + revenueGrowthPercentage
    // (everything else the endpoint returns is all-time). So Apply re-fetches
    // but updates ONLY those two cards — the all-time charts (trends,
    // breakdowns, counts) are left untouched, no flicker / reinit.
    this.refreshDateFilteredRevenue();
  }

  /** Fetch for the current date range and update only the two date-scoped
   *  metrics (Total Revenue + Growth %). Backend filters just these by
   *  startDate/endDate; the rest of the response is all-time and ignored here. */
  private refreshDateFilteredRevenue() {
    this.dashboardService.getDashboard(this.buildDateParams()).subscribe(data => {
      const res = data.data as DashboardResponseModule;
      this.totalRevenue = res?.totalRevenue ?? 0;
      this.revenueGrowthPercent = res?.revenueGrowthPercentage ?? 0;
    });
  }

  /** Build the startDate/endDate query params from the picked local days. */
  private buildDateParams(): { startDate?: string; endDate?: string } {
    const params: { startDate?: string; endDate?: string } = {};
    if (this.startDate) {
      // Backend compares against the exact instant (inclusive, UTC, no snapping)
      // — anchor the start to 00:00:00.000 of the picked local day.
      params.startDate = this.startOfDayIso(this.startDate);
    }
    if (this.endDate) {
      // Backend does NOT snap endDate to end-of-day, so sending "now" would
      // truncate the last day. Send end-of-day so the selected end day is
      // fully included.
      params.endDate = this.endOfDayIso(this.endDate);
    }
    return params;
  }

  /** Local-midnight of the picked day, as a UTC ISO instant. The user picks a
   *  local calendar day; this sends the instant that day *starts* (e.g. Saudi
   *  2026-07-01 00:00 → 2026-06-30T21:00:00.000Z). Backend compares UTC
   *  instants, so this keeps "July 1" meaning the local July 1. */
  private startOfDayIso(d: Date): string {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0).toISOString();
  }

  /** Local end-of-day (23:59:59.999) of the picked day, as a UTC ISO instant —
   *  so the whole selected end day is included (backend does not snap). */
  private endOfDayIso(d: Date): string {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).toISOString();
  }

  onTopProductsViewChange() {
    this.updateTopProductsView();
  }

  private updateTopProductsView() {
    switch (this.topProductsView) {
      case 'revenue':
        this.topProducts = this.topProductsByRevenue;
        break;
      case 'orders':
        this.topProducts = this.topProductsByOrderCount;
        break;
      case 'quantity':
        this.topProducts = this.topProductsByQuantity;
        break;
      default:
        this.topProducts = this.topProductsByRevenue;
    }
  }

  onRevenueTrendPeriodChange() {
    this.updateRevenueTrendChart();
  }

  private updateRevenueTrendChart() {
    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--text-color');
    const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');
    const surfaceBorder = documentStyle.getPropertyValue('--surface-border');

    let trendData: RevenueTrendItem[] = [];

    if (this.revenueTrendPeriod === 'daily') {
      trendData = this.revenueTrendDaily;
    } else if (this.revenueTrendPeriod === 'weekly') {
      trendData = this.revenueTrendWeekly;
    } else {
      trendData = this.revenueTrendMonthly;
    }

    // Format labels based on view type
    const labels = trendData.map(item => this.formatTrendLabel(item.date, this.revenueTrendPeriod));
    const data = trendData.map(item => item.revenue);

    this.revenueTrendChartData = {
      labels: labels,
      datasets: [
        {
          label: this.translate.instant('dashboard.revenueLabel'),
          data: data,
          fill: true,
          backgroundColor: (context: any) => {
            const chart = context.chart;
            const { ctx, chartArea } = chart;
            if (!chartArea) return 'rgba(1, 212, 126, 0)';
            const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
            gradient.addColorStop(0, 'rgba(1, 212, 126, 0.35)');
            gradient.addColorStop(1, 'rgba(1, 212, 126, 0.0)');
            return gradient;
          },
          borderColor: documentStyle.getPropertyValue('--green'),
          borderWidth: 2,
          tension: 0.4,
          pointBackgroundColor: documentStyle.getPropertyValue('--green'),
          pointRadius: 4,
          pointHoverRadius: 6
        }
      ]
    };

    this.revenueTrendChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          labels: {
            color: textColor
          }
        }
      },
      scales: {
        x: {
          ticks: {
            color: textColorSecondary
          },
          grid: {
            color: surfaceBorder,
            drawBorder: false
          }
        },
        y: {
          ticks: {
            color: textColorSecondary
          },
          grid: {
            color: surfaceBorder,
            drawBorder: false
          }
        }
      }
    };
  }

  private formatTrendLabel(dateString: string, period: string): string {
    const date = new Date(dateString);
    // Backend buckets trend dates in UTC (day → T00:00:00Z, week → Monday
    // T00:00:00Z, month → 1st T00:00:00Z). Format in UTC too (timeZone: 'UTC')
    // so a label never drifts to the neighbouring day/month in the viewer's
    // local timezone. Locale follows the active UI language so Arabic mode
    // doesn't leave English month abbreviations on the chart axis.
    const locale = this.localeService.current === 'ar' ? 'ar-SA' : 'en-US';
    if (period === 'monthly') {
      // "Jan 2025"
      return date.toLocaleDateString(locale, { month: 'short', year: 'numeric', timeZone: 'UTC' });
    }
    // daily / weekly → "Jan 1"
    return date.toLocaleDateString(locale, { month: 'short', day: 'numeric', timeZone: 'UTC' });
  }

  private initRevenueTrendChart() {
    this.updateRevenueTrendChart();
  }

  /** hex ('#rrggbb') → {r,g,b}. Lets us derive an opacity ramp from a single
   *  CSS custom-property color instead of hand-picking a rgba() per rank. */
  private hexToRgb(hex: string): { r: number; g: number; b: number } {
    const clean = hex.trim().replace('#', '');
    const full = clean.length === 3 ? clean.split('').map(c => c + c).join('') : clean;
    const num = parseInt(full, 16);
    return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
  }

  /** Single-hue opacity ramp (strongest → lightest) for a ranked bar chart —
   *  color reinforces rank order instead of just differentiating bars, and
   *  stays inside the dashboard's own accent palette (--chart-color-*). */
  private rankedBarColors(hex: string, count: number): string[] {
    const { r, g, b } = this.hexToRgb(hex);
    return Array.from({ length: count }, (_, i) => {
      const opacity = count <= 1 ? 1 : 1 - (i / (count - 1)) * 0.55;
      return `rgba(${r}, ${g}, ${b}, ${opacity.toFixed(2)})`;
    });
  }

  /** Shared options for the horizontal "top 10" ranked bar charts (category /
   *  supplier revenue breakdown). No legend needed — the y-axis labels ARE
   *  the legend — which is also why these charts fit in a shorter box than
   *  the donuts they replaced (no legend area to reserve room for). */
  private rankedBarOptions(textColor: string, textColorSecondary: string, surfaceBorder: string) {
    return {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx: any) => this.formatCurrency(ctx.parsed.x)
          }
        }
      },
      scales: {
        x: {
          beginAtZero: true,
          ticks: { color: textColorSecondary },
          grid: { color: surfaceBorder, drawBorder: false }
        },
        y: {
          ticks: { color: textColor },
          grid: { display: false }
        }
      }
    };
  }

  private buildRevenueByCategoryChart() {
    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--text-color');
    const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');
    const surfaceBorder = documentStyle.getPropertyValue('--surface-border');
    // Cyan — same hue as the Standalone Restaurants KPI card accent, so the
    // "category" story reads as one color family across the dashboard.
    const accent = documentStyle.getPropertyValue('--chart-color-2');

    const labels = this.revenueByCategory.map(c => this.categoryDisplayName(c.id, c.name));
    const data = this.revenueByCategory.map(c => c.revenue);
    const colors = this.rankedBarColors(accent, data.length);

    this.revenueByCategoryChartData = {
      labels: labels,
      datasets: [
        {
          data: data,
          backgroundColor: colors,
          hoverBackgroundColor: colors.map(c => c.replace(/[\d.]+\)$/, '1)')),
          borderRadius: 4,
          maxBarThickness: 22
        }
      ]
    };

    this.revenueByCategoryChartOptions = this.rankedBarOptions(textColor, textColorSecondary, surfaceBorder);
  }

  private buildRevenueBySupplierChart() {
    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--text-color');
    const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');
    const surfaceBorder = documentStyle.getPropertyValue('--surface-border');
    // Blue — matches the Standalone Hotels / Total Revenue accent, keeping the
    // "supplier" story visually distinct from the "category" story above.
    const accent = documentStyle.getPropertyValue('--chart-color-3');

    const labels = this.revenueBySupplier.map(s => s.name);
    const data = this.revenueBySupplier.map(s => s.revenue);
    const colors = this.rankedBarColors(accent, data.length);

    this.revenueBySupplierChartData = {
      labels: labels,
      datasets: [
        {
          data: data,
          backgroundColor: colors,
          hoverBackgroundColor: colors.map(c => c.replace(/[\d.]+\)$/, '1)')),
          borderRadius: 4,
          maxBarThickness: 22
        }
      ]
    };

    this.revenueBySupplierChartOptions = this.rankedBarOptions(textColor, textColorSecondary, surfaceBorder);
  }

  private refreshOrdersChart(){
    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--text-color');

    this.orderChartData = {
      labels: [
        this.translate.instant('dashboard.ordersChartApproved'),
        this.translate.instant('dashboard.ordersChartRejected'),
        this.translate.instant('dashboard.ordersChartCanceled')
      ],
      datasets: [
        {
          data: [this.dashboardResponseModule?.approvedOrdersCount,
            this.dashboardResponseModule?.rejectedOrdersCount,
            this.dashboardResponseModule?.canceledOrdersCount],
          backgroundColor: [documentStyle.getPropertyValue('--p-cyan-500'), documentStyle.getPropertyValue('--p-orange-500'), documentStyle.getPropertyValue('--p-gray-500')],
          hoverBackgroundColor: [documentStyle.getPropertyValue('--p-cyan-400'), documentStyle.getPropertyValue('--p-orange-400'), documentStyle.getPropertyValue('--p-gray-400')]

        }

      ]
    };

    this.orderChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '65%',
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            color: textColor,
            padding: 16
          }
        }
      }
    };


    this.refreshProductsChart();
  }

  /** (Re)build the Products Overview bar with the current locale's labels.
   *  Called on data load AND on locale change — otherwise the labels stay in
   *  whatever language was active when the data first arrived. */
  /** The three product-overview bars, colored from the shared accent palette
   *  (indigo / teal / orange) instead of the previous unrelated rgba() triple
   *  — so this chart reads as part of the same system as the KPI cards and
   *  the revenue breakdown bars, not a fourth palette. */
  private productsChartColors() {
    const documentStyle = getComputedStyle(document.documentElement);
    const hexes = [
      documentStyle.getPropertyValue('--chart-color-4'), // total — indigo
      documentStyle.getPropertyValue('--chart-color-8'), // published — teal
      documentStyle.getPropertyValue('--chart-color-6')  // request-offer — orange
    ];
    return {
      background: hexes.map(hex => {
        const { r, g, b } = this.hexToRgb(hex);
        return `rgba(${r}, ${g}, ${b}, 0.2)`;
      }),
      border: hexes.map(hex => {
        const { r, g, b } = this.hexToRgb(hex);
        return `rgb(${r}, ${g}, ${b})`;
      })
    };
  }

  private refreshProductsChart(){
    const colors = this.productsChartColors();
    this.ProductsChartData = {
      labels: [
        this.translate.instant('dashboard.productsChartTotal'),
        this.translate.instant('dashboard.productsChartPublished'),
        this.translate.instant('dashboard.productsChartRequestOffer')
      ],
      datasets: [
        {
          label: this.translate.instant('dashboard.productsLabel'),
          data: [this.dashboardResponseModule?.productsCount,
            this.dashboardResponseModule?.publishedProductsCount,
            this.dashboardResponseModule?.requestOfferProductsCount],
          backgroundColor: colors.background,
          borderColor: colors.border,
          borderWidth: 1
        }
      ]
    };
  }

  private initCharts(){
    this.orderChartData = {
      labels: [
        this.translate.instant('dashboard.ordersChartApproved'),
        this.translate.instant('dashboard.ordersChartRejected'),
        this.translate.instant('dashboard.ordersChartCanceled')
      ],
      datasets: [{}]
    };

    this.orderChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '65%',
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true
          }
        }
      }
    };



    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--text-color');
    const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');
    const surfaceBorder = documentStyle.getPropertyValue('--surface-border');

    const placeholderColors = this.productsChartColors();
    this.ProductsChartData = {
      labels: [
        this.translate.instant('dashboard.productsChartTotal'),
        this.translate.instant('dashboard.productsChartPublished'),
        this.translate.instant('dashboard.productsChartRequestOffer')
      ],
      datasets: [
        {
          label: this.translate.instant('dashboard.productsLabel'),
          data: [-1, -1, -1],
          backgroundColor: placeholderColors.background,
          borderColor: placeholderColors.border,
          borderWidth: 1
        }
      ]
    };

    this.ProductsChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          // The three bars are data points in ONE dataset, so Chart.js's
          // default legend shows a single "Products" entry. Emit one coloured
          // chip per bar instead (Total / Published / Request-Offer), each
          // taking its own bar's colour — a real legend for the three bars.
          labels: {
            color: textColor,
            usePointStyle: true,
            generateLabels: (chart: any) => {
              const ds = chart.data.datasets[0] ?? {};
              const bg = ds.backgroundColor ?? [];
              const bd = ds.borderColor ?? [];
              return (chart.data.labels ?? []).map((label: string, i: number) => ({
                text: label,
                fillStyle: Array.isArray(bg) ? bg[i] : bg,
                strokeStyle: Array.isArray(bd) ? bd[i] : bd,
                lineWidth: 1,
                hidden: false,
                index: i
              }));
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            color: textColorSecondary
          },
          grid: {
            color: surfaceBorder,
            drawBorder: false
          }
        },
        x: {
          ticks: {
            color: textColorSecondary
          },
          grid: {
            color: surfaceBorder,
            drawBorder: false
          }
        }
      }
    };


  }

  // Pagination methods
  getTotalFiltered(dt: any): number {
    return dt.filteredValue ? dt.filteredValue.length : this.topProducts.length;
  }

  getShowingFrom(): number {
    return this.topProducts.length === 0 ? 0 : this.first + 1;
  }

  getShowingTo(): number {
    const total = this.topProducts.length;
    const end = this.first + this.pageSize;
    return end > total ? total : end;
  }

  getCurrentPage(): number {
    return Math.floor(this.first / this.pageSize) + 1;
  }

  getTotalPages(dt: any): number {
    return Math.ceil(this.getTotalFiltered(dt) / this.pageSize);
  }

  getPageNumbers(dt: any): number[] {
    const total = this.getTotalPages(dt);
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  goToPage(page: number, dt: any): void {
    const newFirst = (page - 1) * this.pageSize;
    this.first = newFirst;
    dt.first = newFirst;
    dt.firstChange.emit(newFirst);
  }

  isFirstPage(): boolean {
    return this.first === 0;
  }

  isLastPage(dt: any): boolean {
    return this.first + this.pageSize >= this.getTotalFiltered(dt);
  }

  previousPage(dt: any): void {
    const newFirst = Math.max(0, this.first - this.pageSize);
    this.first = newFirst;
    dt.first = newFirst;
    dt.firstChange.emit(this.first);
  }

  nextPage(dt: any): void {
    const total = this.getTotalFiltered(dt);
    const newFirst = this.first + this.pageSize;
    if (newFirst < total) {
      this.first = newFirst;
      dt.first = newFirst;
      dt.firstChange.emit(this.first);
    }
  }

  onPageSizeChange(newSize: number, dt: any): void {
    this.pageSize = newSize;
    this.first = 0;
    dt.rows = newSize;
    dt.first = 0;
    dt.firstChange.emit(0);
  }

  onPageChange(event: any): void {
    this.first = event.first;
    this.pageSize = event.rows;
  }

}
